import { useState } from 'react';
import {
  apiAddAutomationTemplate,
  apiAutomationActions,
  apiAutomationTemplates,
  apiListAutomations,
  apiSetAutomationEnabled,
  type AutomationAction,
  type AutomationTemplate,
  type AutomationView,
} from '~/lib/api/automations';
import { classNames } from '~/utils/classNames';
import { draftErrors, FieldsForm } from '~/components/workbench/fields';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Section,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { apiErrorMessage, automationSummary, lastRunLabel } from './automations-model';

export function AutomationsList({
  appId,
  onOpen,
  onRuns,
}: {
  appId: string;
  onOpen: (id: string | null) => void;
  onRuns: (automationId: string) => void;
}) {
  const list = useResource(() => apiListAutomations(appId), appId);
  const actions = useResource(() => apiAutomationActions(appId), `${appId}:actions`);
  const empty = list.data !== null && list.data.length === 0;

  const add = (
    <button onClick={() => onOpen(null)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New automation
    </button>
  );

  return (
    <>
      <Section title="Your automations" icon="i-ph:lightning" aside={!empty && add}>
        {list.loading && !list.data ? (
          <SkeletonCard lines={3} />
        ) : list.error ? (
          <LoadError message="Could not load automations." onRetry={() => void list.reload()} />
        ) : empty ? (
          <div className={classNames(CARD, 'px-5 py-7 text-center')}>
            <div className="i-ph:lightning mx-auto mb-3 text-2xl text-faint" />
            <p className="ui-serif text-[20px] leading-tight text-primary">Nothing automated yet</p>
            <p className="mx-auto mt-2 max-w-[300px] text-[12px] leading-relaxed text-muted">
              Start from a template below, build one yourself, or ask the agent (“email me when an order over ₹5,000
              comes in”).
            </p>
            <button onClick={() => onOpen(null)} className={classNames(BTN_SECONDARY, 'mt-4')}>
              <div className="i-ph:plus text-[13px]" />
              New automation
            </button>
          </div>
        ) : (
          <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
            {list.data!.map((a) => (
              <AutomationRowItem
                key={a.id}
                appId={appId}
                automation={a}
                actions={actions.data ?? []}
                onOpen={() => onOpen(a.id)}
                onRuns={() => onRuns(a.id)}
                onUpdated={(next) => list.setData((rows) => (rows ?? []).map((r) => (r.id === next.id ? next : r)))}
              />
            ))}
          </div>
        )}
      </Section>

      <TemplatesGallery
        appId={appId}
        onAdded={(a) => {
          list.setData((rows) => [a, ...(rows ?? [])]);
        }}
      />
    </>
  );
}

function AutomationRowItem({
  appId,
  automation,
  actions,
  onOpen,
  onRuns,
  onUpdated,
}: {
  appId: string;
  automation: AutomationView;
  actions: AutomationAction[];
  onOpen: () => void;
  onRuns: () => void;
  onUpdated: (next: AutomationView) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (enabled: boolean) => {
    setBusy(true);
    setError(null);

    try {
      onUpdated(await apiSetAutomationEnabled(appId, automation.id, enabled));
    } catch (err) {
      setError(apiErrorMessage(err, enabled ? 'Could not turn it on.' : 'Could not turn it off.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <button onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="truncate text-[13px] font-medium text-primary">{automation.name}</div>
          <div className="mt-0.5 truncate text-[11px] text-muted">{automationSummary(automation, actions)}</div>
        </button>
        <Switch
          checked={automation.enabled}
          onChange={(v) => void toggle(v)}
          label={automation.enabled ? 'On' : 'Off'}
          disabled={busy}
        />
      </div>
      <div className="flex items-center gap-3 text-[11px] text-muted">
        <span>{lastRunLabel(automation.lastRunAt)}</span>
        <span aria-hidden>·</span>
        <span>
          {automation.runCount} run{automation.runCount === 1 ? '' : 's'}
        </span>
        <button
          onClick={onRuns}
          className="ml-auto text-secondary underline-offset-2 hover:text-primary hover:underline"
        >
          Run log
        </button>
        <button onClick={onOpen} className="text-secondary underline-offset-2 hover:text-primary hover:underline">
          Edit
        </button>
      </div>
      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function TemplatesGallery({ appId, onAdded }: { appId: string; onAdded: (a: AutomationView) => void }) {
  const templates = useResource(() => apiAutomationTemplates(appId), `${appId}:templates`);
  const [open, setOpen] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());

  return (
    <Section title="Templates" icon="i-ph:sparkle">
      {templates.loading && !templates.data ? (
        <SkeletonCard lines={2} />
      ) : templates.error ? (
        <LoadError message="Could not load templates." onRetry={() => void templates.reload()} />
      ) : (
        <div className="grid gap-2">
          {(templates.data ?? []).map((t) => (
            <TemplateCard
              key={t.id}
              appId={appId}
              template={t}
              expanded={open === t.id}
              added={added.has(t.id)}
              onExpand={() => setOpen(open === t.id ? null : t.id)}
              onAdded={(a) => {
                setAdded((s) => new Set(s).add(t.id));
                setOpen(null);
                onAdded(a);
              }}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function TemplateCard({
  appId,
  template,
  expanded,
  added,
  onExpand,
  onAdded,
}: {
  appId: string;
  template: AutomationTemplate;
  expanded: boolean;
  added: boolean;
  onExpand: () => void;
  onAdded: (a: AutomationView) => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(template.params.filter((p) => p.default !== undefined).map((p) => [p.key, p.default])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = draftErrors(template.params, values);
  const needsParams = template.params.length > 0;

  const add = async () => {
    if (Object.keys(errors).length) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      onAdded(await apiAddAutomationTemplate(appId, template.id, values));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add this automation.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classNames(CARD, 'space-y-3 p-3.5')}>
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-hairline bg-sunken">
          <div className={classNames(template.icon, 'text-[14px] text-secondary')} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-medium text-primary">{template.title}</div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted">{template.description}</p>
        </div>
        {added ? (
          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-success">
            <div className="i-ph:check text-[12px]" />
            Added
          </span>
        ) : (
          <button
            onClick={() => (needsParams && !expanded ? onExpand() : void add())}
            disabled={busy}
            className={classNames(BTN_SECONDARY, 'shrink-0')}
          >
            {busy ? <Spinner /> : <div className="i-ph:plus text-[13px]" />}
            Add
          </button>
        )}
      </div>
      {expanded && !added && (
        <div className="space-y-3 border-t border-hairline pt-3">
          <FieldsForm fields={template.params} values={values} onChange={setValues} errors={errors} disabled={busy} />
          <div className="flex justify-end gap-2">
            <button onClick={onExpand} className={BTN_GHOST}>
              Cancel
            </button>
            <button
              onClick={() => void add()}
              disabled={busy || Object.keys(errors).length > 0}
              className={BTN_SECONDARY}
            >
              {busy && <Spinner />}
              Add automation
            </button>
          </div>
        </div>
      )}
      {error && <ErrorBanner message={error} />}
    </div>
  );
}
